import * as d3 from 'd3';
import _ from 'lodash';
import { Granularity } from 'models';
import { IChartPoint, IChartTimeline } from './models';

export const ChartConstants = {
    xAxisLabelHeight: 20, // allocated fixed size for label heights
    xAxisGutterPct: 0.15, // reserve 15% for left/right gutter
    yAxisLabelHeight: 16, // allocated fixed size for label heights
    yAxisLabelOffset: 8, // gap between yAxis label and line
    dotRadius: 3, // radius for dots in chart lines
    dotStrokeWidth: 2, // stroke-width for dots in chart lines
};

export const Transition = {
    growIn: (selection: d3.Selection<any, any, any, any>): void => {
        selection
            .style('opacity', 0.4)
            .style('transform', 'translateY(4px)')
            .transition()
            .duration(400)
            .ease(d3.easeLinear)
            .style('opacity', 1)
            .style('transform', 'translateY(0)');
    },
    haloIn: (selection: d3.Selection<any, any, any, any>): void => {
        selection
            .style('opacity', 0.4)
            .style('transform', 'translateY(-4px)')
            .transition()
            .duration(400)
            .ease(d3.easeCubic)
            .style('opacity', 1)
            .style('transform', 'translateY(0px)');
    },
    slideIn: (selection: d3.Selection<any, any, any, any>): void => {
        selection
            .style('transform', 'translateY(-4px)')
            .transition()
            .duration(400)
            .ease(d3.easeLinear)
            .style('transform', 'translateY(0px)');
    },
    slideOut: (selection: d3.Selection<any, any, any, any>): void => {
        selection.transition().duration(100).ease(d3.easeCubic).style('transform', 'translateY(-4px)').remove();
    },
    fadeIn: (selection: d3.Selection<any, any, any, any>): void => {
        selection.style('opacity', 0.4).transition().duration(400).ease(d3.easeLinear).style('opacity', 1);
    },
    fadeOut: (selection: d3.Selection<any, any, any, any>): void => {
        selection.transition().duration(100).ease(d3.easeCubic).style('opacity', 0).remove();
    },
};

// replace Gran with SpanType ?
export const getInterval = (granularity: Granularity): d3.CountableTimeInterval => {
    let interval: d3.CountableTimeInterval;
    switch (granularity) {
        case Granularity.Monthly:
            interval = d3.utcMonth;
            break;
        case Granularity.Weekly:
            interval = d3.utcWeek;
            break;
        case Granularity.Daily:
        default:
            interval = d3.utcDay;
    }
    return interval;
};

export const getDateFormat = (granularity: Granularity, wide?: boolean): ((d: Date) => string) => {
    let dateFormat: string;
    switch (granularity) {
        case Granularity.Monthly:
            dateFormat = wide ? '%B %Y' : '%b';
            break;
        case Granularity.Weekly:
            dateFormat = wide ? 'Week %W' : 'Wk %W';
            break;
        case Granularity.Daily:
        default:
            dateFormat = wide ? '%B%e' : '%b%e';
    }
    return d3.utcFormat(dateFormat);
};

export const sumByGranularity = (granularity: Granularity, chartPoints: IChartPoint[]): IChartPoint[] => {
    const interval = getInterval(granularity);
    const grouped = _.groupBy(chartPoints, point => interval.floor(point.date));
    return _.map(_.values(grouped), group => {
        return {
            date: interval.floor(group[0].date),
            amount: _.sum(_.map(group, 'amount')),
        };
    });
};

// Given a timeline with discrete data, generate a version with summarized data, based
// on the provided granularity
export const summarizeTimeline = (
    timeline: IChartTimeline | Error | undefined,
    granularity: Granularity | undefined,
): IChartTimeline | undefined => {
    if (timeline && !_.isError(timeline) && granularity) {
        const { startDate, endDate, claimTypes, data } = timeline;
        // NB: timelint data is stored
        const summarized = _.map(data, (line: IChartPoint[]): IChartPoint[] => {
            return sumByGranularity(granularity, line);
        });
        return {
            startDate,
            endDate,
            claimTypes,
            data: summarized,
        };
    }
};

// create X and Y axis scales for the provided timeline
export const createScales = (
    timeline: IChartTimeline,
    granularity: Granularity,
    chartRect: ClientRect,
): [d3.ScaleTime<number, number>, d3.ScaleLinear<number, number>] => {
    // compute x-axis boundaries
    const interval = getInterval(granularity);
    const xScale = d3
        .scaleTime()
        .domain([interval.floor(timeline.startDate), interval.floor(timeline.endDate)])
        .range([chartRect.left, chartRect.right]);

    // compute y-axis boundaries based on
    const amounts = _.flatMap(timeline.data, (arr: IChartPoint[]): number[] => {
        return _.map(arr, 'amount');
    });
    const yMax = _.max(amounts) as number;
    const yMin = _.min(amounts) as number;
    const yPrecision = _.size(String(_.round(yMax - yMin))) - 1;
    let yDomainStart = 0;
    let yDomainEnd = _.ceil(yMax, -yPrecision);
    if (yDomainStart === yDomainEnd) {
        yDomainStart = 0;
        yDomainEnd = Number.MAX_VALUE;
    }
    const yScale = d3.scaleLinear().domain([yDomainStart, yDomainEnd]).range([chartRect.bottom, chartRect.top]);
    return [xScale, yScale];
};

export const createChartRect = (clientRect: ClientRect): ClientRect => {
    const chartHeight = clientRect.height - 2 * ChartConstants.xAxisLabelHeight;
    return {
        height: chartHeight,
        width: clientRect.width,
        left: clientRect.width * ChartConstants.xAxisGutterPct,
        right: clientRect.width * (1 - ChartConstants.xAxisGutterPct),
        top: ChartConstants.yAxisLabelHeight + ChartConstants.yAxisLabelOffset, // provide space at top for yAxisLabels
        bottom: chartHeight,
    };
};

export const timelineLabels = {
    cashFlow: ['Cash In', 'Cash Out'],
    claimVolume: ['Charge Amount', 'Payer Paid', 'Patient Paid'],
    patientCollections: ['Patients Responsibility', 'Patients Paid', 'Patient Refund'],
    practiceSummary: ['Charges', 'Adjustments', 'Payments'],
};
